// Design tokens pulled from Figma (Loyalty Cloud → "Log In" page, node 18050:151 and
// siblings) via get_variable_defs + get_design_context. Replace/extend as more pages
// get their new-UI pass — this file is the single source of truth for theme values.
import React from 'react';
import PropTypes from 'prop-types';
import {CssBaseline, ThemeProvider, createTheme} from '@mui/material';

// Raw design tokens (Figma variable name -> value). Kept alongside the MUI theme so
// components that need a token MUI's palette doesn't model (e.g. neutral/100, the
// success/info/error "50" badge backgrounds) can import it directly.
export const tokens = {
    color: {
        neutral50: '#fafafa',
        neutral100: '#f5f5f5',
        neutral200: '#e5e5e5',
        neutral300: '#d4d4d4',
        neutral500: '#737373',
        neutral600: '#525252',
        neutral700: '#404040',
        neutral950: '#0a0a0a',
        sky50: '#f0f9ff',
        sky600: '#0084d1',
        error50: '#fef2f2',
        error500: '#fb2c36',
        success50: '#effdf5',
        success200: '#b3f5d1',
        success500: '#00c16a',
        success700: '#00a155',
        info50: '#eff6ff',
        white: '#ffffff'
    },
    radius: {
        card: '12px',
        control: '6px',
        toast: '8px',
        badge: '100px'
    },
    shadow: {
        card: '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px 0px rgba(0,0,0,0.06)',
        toast: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -2px rgba(0,0,0,0.05)'
    }
};

const theme = createTheme({
    typography: {
        fontFamily: "'Inter', sans-serif",
        h4: {fontSize: '20px', fontWeight: 600, lineHeight: '28px'} // text-xl/semibold
    },
    palette: {
        background: {default: tokens.color.neutral50},
        primary: {main: tokens.color.sky600},
        secondary: {main: tokens.color.sky600},
        error: {main: tokens.color.error500},
        text: {primary: tokens.color.neutral950, secondary: tokens.color.neutral500},
        divider: tokens.color.neutral200
    },
    shape: {borderRadius: 6},
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    height: 40,
                    borderRadius: tokens.radius.control,
                    boxShadow: 'none'
                },
                outlined: {
                    borderColor: tokens.color.neutral300,
                    color: tokens.color.neutral950,
                    '&:hover': {borderColor: tokens.color.neutral300, backgroundColor: tokens.color.neutral50}
                },
                contained: {
                    '&:hover': {boxShadow: 'none'}
                }
            }
        },
        MuiTextField: {
            defaultProps: {size: 'small'}
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: tokens.radius.control,
                    fontSize: '14px',
                    '& fieldset': {borderColor: tokens.color.neutral300},
                    '&:hover fieldset': {borderColor: tokens.color.neutral300},
                    '&.Mui-focused fieldset': {borderColor: tokens.color.sky600, borderWidth: '1px'},
                    '&.Mui-error fieldset': {borderColor: tokens.color.error500}
                }
            }
        }
    }
});

const ThemeCustomization = ({children}) => (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
    </ThemeProvider>
);

ThemeCustomization.propTypes = {
    children: PropTypes.node
};

export default ThemeCustomization;
